'use client';

import { useEffect, useRef, type RefObject } from 'react';

interface ScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

/**
 * Lightweight scroll-reveal hook using Intersection Observer.
 * Adds `.revealed` class when element enters viewport.
 * Removes `will-change` after transition completes.
 */
export function useScrollReveal<T extends HTMLElement>(
  ref: RefObject<T | null>,
  options: ScrollRevealOptions = {},
): void {
  const { threshold = 0.15, rootMargin = '0px 0px -50px 0px', once = true } = options;
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Respect prefers-reduced-motion — reveal immediately
    const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!motionOk) {
      element.classList.add('revealed');
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add('revealed');
          // Remove will-change after animation completes
          element.addEventListener(
            'transitionend',
            () => { element.style.willChange = 'auto'; },
            { once: true },
          );
          if (once) observerRef.current?.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    observerRef.current.observe(element);

    return () => { observerRef.current?.disconnect(); };
  }, [ref, threshold, rootMargin, once]);
}
